// Carry-over (V7 step 2, Isaac): A2000 splits and merges orders, never the app. When a pick ticket
// shows up in another WMS than the line that last held it, that line's entries come with it.
// Pure, and worked out on every read (`assemble.ts`), so nothing runs "once": the same store and
// source always give the same lines, and a person's next save of the line keeps the result.
//
// - Sources: the stored lines its pick tickets were on. A WMS line → the receiving line is Split; a
//   line from before WMS grouping (the move-over) → New, or Approved when every source was.
// - Every entry comes, except SCHEDULED SHIP DATE from a WMS line (it stays blank there); the move-
//   over brings that too.
// - The line's own value stays. A blank field is filled when the sources agree; when they do not —
//   or when one disagrees with the line's own value — nobody wins: the line's own value (or blank)
//   stays and the competing values are kept (`carryConflicts`) for the team to choose.
// - A line carries from each source once: a save keeps the source in `carriedFrom`, and a source
//   named there is never carried from again (so a value chosen, or cleared, stays).

import type { LineView } from "./assemble";
import { ptText } from "./pt-text";
import type { WarehouseField, WarehouseValues } from "./types";
import { WAREHOUSE_FIELDS } from "./types";

/** A line its pick tickets came from. */
export interface CarrySource {
  lineId: string;
  /** Its WMS #; "" for a line from before WMS grouping and a Deposco line. */
  wms: string;
  /** From before WMS grouping: the move-over (SCHEDULED SHIP DATE comes too). */
  legacy: boolean;
  approved: boolean;
  fields: WarehouseValues;
  /** What the team knows of it (`sourceAbout`): "from WMS 37092 · TJ MAXX VIA EDI · PT 1807695". */
  about?: string;
}

export interface ConflictValue {
  value: string;
  fromLine: string;
  fromWms: string;
  /** Where it came from, as the team knows it; kept with the value, as the source line may change. */
  about?: string;
}
/** Field → the values that competed for it. */
export type CarryConflicts = Partial<Record<WarehouseField, ConflictValue[]>>;

/** An entry nobody has made: blank, or the routing status a new line starts with. */
export const isBlankEntry = (field: WarehouseField, v: string) =>
  !v || (field === "routingStatus" && v === "Not routed");

/** The entries carried: every field but SCHEDULED SHIP DATE from a WMS line. */
const carries = (field: WarehouseField, s: CarrySource) => field !== "schedShip" || s.legacy;

export function carryOver(
  own: WarehouseValues,
  sources: readonly CarrySource[],
): { fields: WarehouseValues; conflicts: CarryConflicts; filled: WarehouseField[] } {
  const fields = { ...own };
  const conflicts: CarryConflicts = {};
  const filled: WarehouseField[] = [];
  for (const field of WAREHOUSE_FIELDS) {
    const offered: ConflictValue[] = sources
      .filter((s) => carries(field, s) && !isBlankEntry(field, s.fields[field]))
      .map((s) => ({
        value: s.fields[field],
        fromLine: s.lineId,
        fromWms: s.wms,
        ...(s.about ? { about: s.about } : {}),
      }));
    if (!offered.length) continue;
    const mine = own[field];
    if (!isBlankEntry(field, mine)) {
      const other = offered.filter((o) => o.value !== mine);
      if (other.length) conflicts[field] = other;
      continue;
    }
    if (new Set(offered.map((o) => o.value)).size === 1) {
      (fields as Record<WarehouseField, string>)[field] = offered[0]!.value;
      filled.push(field);
    } else conflicts[field] = offered;
  }
  return { fields, conflicts, filled };
}

/** Both sets, each value once per field. */
export function mergeConflicts(a: CarryConflicts, b: CarryConflicts): CarryConflicts {
  const out: CarryConflicts = { ...a };
  for (const [field, list] of Object.entries(b) as [WarehouseField, ConflictValue[]][]) {
    const had = out[field] ?? [];
    const more = list.filter((v) => !had.some((h) => h.value === v.value && h.fromLine === v.fromLine));
    if (had.length || more.length) out[field] = [...had, ...more];
  }
  return out;
}

/** A field chosen: its conflict is settled. */
export function withoutConflict(c: CarryConflicts, field: WarehouseField): CarryConflicts {
  const { [field]: _gone, ...rest } = c;
  return rest;
}

const MAX_JSON = 4000;

/** `carryConflicts` as stored: JSON, at most 4,000 characters (long values cut, then extra values dropped). */
export function encodeConflicts(c: CarryConflicts): string {
  const entries = Object.entries(c).filter(([, v]) => v && v.length);
  if (!entries.length) return "";
  const cut = Object.fromEntries(
    entries.map(([f, list]) => [
      f,
      list.map((v) => ({
        value: v.value.slice(0, 200),
        fromLine: v.fromLine,
        fromWms: v.fromWms,
        ...(v.about ? { about: v.about.slice(0, 160) } : {}),
      })),
    ]),
  );
  let json = JSON.stringify(cut);
  while (json.length > MAX_JSON) {
    const longest = Object.keys(cut).sort((x, y) => cut[y]!.length - cut[x]!.length)[0]!;
    if (cut[longest]!.length > 2) cut[longest]!.pop();
    else delete cut[longest];
    json = Object.keys(cut).length ? JSON.stringify(cut) : "";
  }
  return json;
}

/** `carryConflicts` read back; anything unreadable is no conflict (the integrity check names it). */
export function decodeConflicts(text: string | null | undefined): CarryConflicts {
  if (!text) return {};
  try {
    const raw = JSON.parse(text) as Record<string, unknown>;
    const out: CarryConflicts = {};
    for (const field of WAREHOUSE_FIELDS) {
      const list = raw[field];
      if (!Array.isArray(list)) continue;
      const ok = list
        .filter(
          (v): v is ConflictValue =>
            !!v && typeof v.value === "string" && typeof v.fromLine === "string" && typeof v.fromWms === "string",
        )
        .map((v) => ({ ...v, ...(typeof v.about === "string" ? {} : { about: undefined }) }))
        .map(({ about, ...v }) => (about ? { ...v, about } : v));
      if (ok.length) out[field] = ok;
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * A source as the team knows it, from the pick tickets that came: "from WMS 37092 · TJ MAXX VIA EDI ·
 * PT 1807695" (`place` "WMS 37092"; a Deposco line's "PO 50264439 · DC 699", its SOs "SO …"); a
 * line from before WMS grouping has no place, so its customer, pick tickets and ship window:
 * "TJ MAXX VIA EDI · PT 1807695-1807696 · ship 10/22–10/28".
 */
export function sourceAbout(
  place: string,
  pts: readonly Pick<LineView["pts"][number], "pickTicket" | "customerName" | "start" | "cancel" | "gone">[],
  word = "PT",
): string {
  const customers = [...new Set(pts.map((p) => p.customerName).filter(Boolean))].join(", ");
  const ptsText = pts.length ? `${word} ${ptText({ pts: pts.map((p) => ({ ...p, gone: false })) })}` : "";
  const day = (d: string | null) => (d ? `${d.slice(5, 7)}/${d.slice(8, 10)}` : "—");
  const first = (xs: (string | null)[]) => xs.filter((x): x is string => !!x).sort()[0] ?? null;
  const ship = `ship ${day(first(pts.map((p) => p.start)))}–${day(first(pts.map((p) => p.cancel)))}`;
  return (place ? [`from ${place}`, customers, ptsText] : [customers, ptsText, ship]).filter(Boolean).join(" · ");
}

/** The line ids `carriedFrom` names: the sources a line has already carried from. */
export const carriedIds = (text: string | null | undefined) => new Set((text ?? "").match(/\b[A-Z]{2,}-[A-Z0-9]+\b/g));

/**
 * Where a line's entries came from, as `carriedFrom` keeps it — what it had, then the sources this
 * read found, each as the team knows it with its line id: "from WMS 37218 · ROSS STORES · PT 1807695
 * (RL-12); TJ MAXX VIA EDI · PT 1807464 · ship 10/01–10/05 (RL-9)".
 */
export function carriedFromText(sources: readonly Pick<CarrySource, "lineId" | "wms" | "about">[], had = ""): string {
  const known = carriedIds(had);
  const more = sources
    .filter((s) => !known.has(s.lineId))
    .map((s) => `${s.about || (s.wms ? `from WMS ${s.wms}` : "an earlier line")} (${s.lineId})`);
  return [had, ...more].filter(Boolean).join("; ").slice(0, 500);
}

/** `carriedFrom` as the team reads it: the labels, without the line ids kept for "carry once". */
export const carriedFromLabel = (text: string) => text.replace(/ \([A-Z]{2,}-[A-Z0-9]+\)/g, "");

/** The move-over's numbers: what the log says once at boot. */
export function moveOverSummary(lines: readonly LineView[]): {
  oldLines: number;
  wmsLines: number;
  conflicts: number;
  approved: number;
} {
  const from = new Set<string>();
  let wmsLines = 0;
  let conflicts = 0;
  let approved = 0;
  for (const l of lines) {
    const legacy = l.carry.sources.filter((s) => s.legacy);
    if (!legacy.length || l.carry.saved) continue;
    wmsLines++;
    for (const s of legacy) from.add(s.lineId);
    conflicts += Object.keys(l.carry.conflicts).length;
    if (l.review === "approved" || l.review === "updated") approved++;
  }
  return { oldLines: from.size, wmsLines, conflicts, approved };
}
